import React, { useEffect, useRef } from 'react';
import Chart from 'chart.js/auto';

type ChartImageGeneratorProps = {
  type: 'pie' | 'bar';
  data: any;
  options?: any;
  onImageReady: (base64: string) => void;
};

const ChartImageGenerator: React.FC<ChartImageGeneratorProps> = ({ type, data, options, onImageReady }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const ctx = canvasRef.current?.getContext('2d');
    if (!ctx) return;

    // Enhanced default options for modern look
    const defaultOptions = type === 'pie' ? {
      animation: false,
      responsive: false,
      devicePixelRatio: 2,
      plugins: {
        legend: {
          display: true,
          position: 'bottom' as const,
          labels: {
            padding: 15,
            font: {
              size: 11,
              family: "'Inter', 'Helvetica', 'Arial', sans-serif",
              weight: '600' as const,
            },
            color: '#1e293b',
            usePointStyle: true,
            pointStyle: 'circle',
            boxWidth: 8,
            boxHeight: 8,
          },
        },
        tooltip: {
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          titleColor: '#ffffff',
          bodyColor: '#e2e8f0',
          borderColor: '#475569',
          borderWidth: 1,
          padding: 12,
          cornerRadius: 8,
          titleFont: {
            size: 13,
            weight: 'bold' as const,
          },
          bodyFont: {
            size: 12,
          },
          displayColors: true,
          boxWidth: 10,
          boxHeight: 10,
          boxPadding: 6,
        },
      },
      cutout: '65%',
      radius: '90%',
      spacing: 2,
      borderWidth: 0,
    } : {
      animation: false,
      responsive: false,
      devicePixelRatio: 2,
      plugins: {
        legend: {
          display: false,
        },
        tooltip: {
          backgroundColor: 'rgba(15, 23, 42, 0.95)',
          titleColor: '#ffffff',
          bodyColor: '#e2e8f0',
          borderColor: '#475569',
          borderWidth: 1,
          padding: 12,
          cornerRadius: 8,
          titleFont: {
            size: 13,
            weight: 'bold' as const,
          },
          bodyFont: {
            size: 12,
          },
        },
      },
      scales: {
        x: {
          grid: {
            display: false,
          },
          ticks: {
            maxRotation: 0,
            minRotation: 0,
            font: {
              size: 11,
              family: "'Inter', 'Helvetica', 'Arial', sans-serif",
              weight: '600' as const,
            },
            color: '#475569',
          },
          border: {
            display: false,
          },
        },
        y: {
          beginAtZero: true,
          grid: {
            color: '#e2e8f0',
            lineWidth: 1,
          },
          ticks: {
            precision: 0,
            font: {
              size: 11,
              family: "'Inter', 'Helvetica', 'Arial', sans-serif",
            },
            color: '#64748b',
            padding: 8,
          },
          border: {
            display: false,
          },
        },
      },
      borderRadius: 6,
      borderSkipped: false,
    };

    const chart = new Chart(ctx, {
      type,
      data,
      options: {
        ...defaultOptions,
        ...options,
      },
    });

    let animFrameId: any = null;
    let timeoutId: any = null;

    const extractAndNotify = () => {
      if (canvasRef.current) {
        try {
          const base64Png = canvasRef.current.toDataURL('image/png');
          onImageReady(base64Png);
        } catch (e) {
          console.error('Error generating chart image:', e);
        }
      }
      chart.destroy();
    };

    // Use requestAnimationFrame with quick timeout fallback to capture painted canvas across all browsers
    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
      animFrameId = window.requestAnimationFrame(() => {
        extractAndNotify();
      });
    } else {
      timeoutId = setTimeout(extractAndNotify, 16);
    }

    return () => {
      if (animFrameId && typeof window !== 'undefined') window.cancelAnimationFrame(animFrameId);
      if (timeoutId) clearTimeout(timeoutId);
      chart.destroy();
    };
    // eslint-disable-next-line
  }, [type, JSON.stringify(data), JSON.stringify(options)]);

  return (
    <canvas
      ref={canvasRef}
      width={240}
      height={240}
      style={{ position: 'fixed', left: -9999, top: -9999, opacity: 0, pointerEvents: 'none' }}
    />
  );
};

export default ChartImageGenerator; 